import { makeStyles, shorthands, Input, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../../risikoanalyseStep/tokens";

/**
 * Generische Key-Value-Anzeige für die Allgemein-Sektion der Contract Detail
 * View. Funktioniert für alle Kinds. Felder mit `editKey` werden — sofern ein
 * `onChange` übergeben wird — als Eingabefeld gerendert (Inline-Edit).
 */
export type AllgemeinField = {
  label: string;
  value: string | number | null | undefined;
  /** When set (and an onChange handler is provided), the field renders an
   *  editable input bound to this key instead of read-only text. */
  editKey?: string;
  /** Raw value shown in the input in edit mode (unformatted number / ISO date). */
  editValue?: string;
  inputType?: "text" | "date" | "number";
};

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "8px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  field: {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 0.7fr) 1.3fr",
    columnGap: "12px",
    alignItems: "center",
    paddingTop: "6px",
    paddingBottom: "6px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
  },
  label: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  value: {
    fontSize: "0.95rem",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    whiteSpace: "pre-wrap",
  },
  valueMuted: {
    color: BRAND.muted,
    fontStyle: "italic",
  },
  input: {
    width: "100%",
  },
});

const formatValue = (v: AllgemeinField["value"]): { text: string; muted: boolean } => {
  if (v === null || v === undefined || v === "") return { text: "—", muted: true };
  return { text: String(v), muted: false };
};

const AllgemeinDetails = ({
  fields,
  values,
  onChange,
}: {
  fields: AllgemeinField[];
  /** Current edited values keyed by `editKey`. */
  values?: Record<string, string>;
  /** When provided, fields carrying an `editKey` become editable inputs. */
  onChange?: (editKey: string, value: string) => void;
}) => {
  const styles = useStyles();
  return (
    <div className={styles.grid}>
      {fields.map((f, idx) => {
        const editable = !!f.editKey && !!onChange;
        const { text, muted } = formatValue(f.value);
        return (
          <div key={`${f.label}-${idx}`} className={styles.field}>
            <span className={styles.label}>{f.label}</span>
            {editable ? (
              <Input
                className={styles.input}
                type={f.inputType ?? "text"}
                size="small"
                value={values?.[f.editKey as string] ?? f.editValue ?? ""}
                onChange={(_, d) => onChange?.(f.editKey as string, d.value)}
              />
            ) : (
              <span className={mergeClasses(styles.value, muted && styles.valueMuted)}>
                {text}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AllgemeinDetails;
